import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from 'react-toastify';
import toastError from '../../errors/toastError';
import { Helmet } from "react-helmet";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import TelegramIcon from '@mui/icons-material/Telegram';
import FacebookIcon from '@mui/icons-material/Facebook';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ColorModeContext from "../../layout/themeContext";
import config from "../../config";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const { nomeEmpresa, versionSystem } = config;

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100vw",
    minHeight: "100vh",
    display: "flex",
    backgroundColor: theme.palette.background.default,
    overflow: "hidden",
    position: "relative",
  },
  
  // Seção esquerda - Slides
  leftSection: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#4c1d95",
    backgroundImage: `linear-gradient(135deg, #4c1d95 0%, #1e1b4b 100%)`,
    position: "relative",
    overflow: "hidden",
    padding: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      display: "none",
    },
    // Efeito de malha decorativa
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundImage: `radial-gradient(circle at 20% 30%, rgba(101, 23, 171, 0.15) 0%, transparent 50%), 
                        radial-gradient(circle at 80% 70%, rgba(124, 58, 237, 0.15) 0%, transparent 50%)`,
      pointerEvents: "none",
    }
  },

  slideContentWrapper: {
    width: "100%",
    maxWidth: "600px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    zIndex: 2,
  },

  slideContainer: {
    width: "100%",
    height: "auto",
    minHeight: "400px",
    position: "relative",
    marginBottom: theme.spacing(6),
  },

  slide: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transform: "translateY(20px)",
    transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
    "&.active": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  slideContent: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: theme.spacing(5, 4),
    backgroundColor: "rgba(255, 255, 255, 0.04)",
    backdropFilter: "blur(12px)",
    borderRadius: "32px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    boxShadow: "0 24px 48px rgba(0, 0, 0, 0.2)",
  },

  slideTitle: {
    fontSize: "2.25rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: theme.spacing(2),
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
    textShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
  },

  slideDescription: {
    fontSize: "1.125rem",
    color: "#cbd5e1",
    lineHeight: 1.6,
    marginBottom: theme.spacing(4),
    maxWidth: "500px",
    fontWeight: 400,
  },

  socialIconsContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    flexWrap: "wrap",
    justifyContent: "center",
  },

  socialIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
    },
  },

  whatsappIcon: {
    backgroundColor: "#25D366",
    color: "white",
  },

  instagramIcon: {
    background: "linear-gradient(45deg, #405DE6, #5851DB, #833AB4, #C13584, #E1306C, #FD1D1D)",
    color: "white",
  },

  telegramIcon: {
    backgroundColor: "#0088cc",
    color: "white",
  },

  facebookIcon: {
    backgroundColor: "#1877F2",
    color: "white",
  },

  securityIcon: {
    backgroundColor: "rgba(255, 202, 40, 0.15)",
    color: "#ffca28",
    border: "1px solid rgba(255, 202, 40, 0.3)",
    boxShadow: "0 8px 16px rgba(255, 202, 40, 0.1)",
  },

  slideControls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(4),
    marginTop: theme.spacing(2),
    width: "100%",
  },

  slideButton: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },

  slideButtonIcon: {
    color: "white",
    fontSize: "18px",
  },

  slideIndicators: {
    display: "flex",
    gap: theme.spacing(1),
  },

  indicator: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&.active": {
      backgroundColor: "#6517ab",
      width: "24px",
      borderRadius: "4px",
    },
  },

  // Seção direita - Formulário
  rightSection: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.background.default,
    position: "relative",
    overflow: "auto",
    padding: theme.spacing(4),
  },

  loginCard: {
    width: "100%",
    maxWidth: "440px",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "24px",
    padding: theme.spacing(5),
    boxShadow: theme.palette.type === 'light' 
      ? "0 20px 40px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)"
      : "0 20px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)",
    position: "relative",
    zIndex: 2,
  },

  cardHeader: {
    marginBottom: theme.spacing(4),
  },

  logoContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(3),
  },

  logoImg: {
    maxWidth: "280px",
    maxHeight: "80px",
    width: "auto",
    height: "auto",
    objectFit: "contain",
  },

  welcomeTitle: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    textAlign: "center",
    letterSpacing: "-0.02em",
  },

  welcomeSubtitle: {
    fontSize: "0.95rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
    textAlign: "center",
    fontWeight: 400,
    lineHeight: 1.6,
  },

  form: {
    width: "100%",
  },

  inputField: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      backgroundColor: theme.palette.type === 'light' ? "#f8fafc" : "rgba(255, 255, 255, 0.05)",
      border: `2px solid ${theme.palette.divider}`,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      fontSize: "0.95rem",
      fontWeight: 500,
      "&:hover": {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.type === 'light' ? "#ffffff" : "rgba(255, 255, 255, 0.08)",
      },
      "&.Mui-focused": {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.type === 'light' ? "#ffffff" : "rgba(255, 255, 255, 0.08)",
        boxShadow: `0 0 0 4px ${theme.palette.primary.main}20`,
      },
      "& fieldset": {
        border: "none",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "16px 18px",
      color: theme.palette.text.primary,
      fontSize: "0.95rem",
    },
  },

  inputIcon: {
    color: "#6517ab",
    fontSize: "1.2rem",
  },

  submitButton: {
    width: "100%",
    height: "52px",
    borderRadius: "14px",
    background: theme.palette.type === 'light' 
      ? "linear-gradient(135deg, #6517ab 0%, #7c3aed 100%)"
      : "linear-gradient(135deg, hsl(270, 80%, 60%) 0%, hsl(280, 80%, 60%) 100%)",
    color: "white",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: theme.palette.type === 'light'
      ? "0 8px 24px rgba(101, 23, 171, 0.4)"
      : "0 8px 24px rgba(0, 0, 0, 0.3)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.palette.type === 'light'
        ? "0 12px 32px rgba(101, 23, 171, 0.5)"
        : "0 12px 32px rgba(0, 0, 0, 0.4)",
      backgroundColor: theme.palette.primary.main,
    },
  },

  backToLogin: {
    textAlign: "center",
    marginTop: theme.spacing(2),
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "none",
      fontWeight: 600,
      fontSize: "0.875rem",
      "&:hover": {
        textDecoration: "underline",
        color: theme.palette.primary.dark,
      },
    },
  },

  visibilityIcon: {
    color: "#94a3b8",
  },

  decorationCircle: {
    position: "absolute",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(101, 23, 171, 0.1) 0%, rgba(101, 23, 171, 0) 70%)",
    pointerEvents: "none",
  },

  platformIndicator: {
    position: "absolute",
    bottom: theme.spacing(4),
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(3),
  },

  platformItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "0.75rem",
    fontWeight: 500,
  },

  platformDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#6517ab",
  },
}));

const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

const ForgetPassword = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName } = colorMode;

  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [systemVersion, setSystemVersion] = useState("");

  // Slides (copiado do Login)
  const slides = [
    {
      id: 1,
      title: "Centralize Todas as Conversas",
      description: "Conecte WhatsApp, Instagram, Telegram e Facebook em uma única plataforma. Gerencie todas as mensagens de diferentes redes sociais em um só lugar.",
      icons: [
        { icon: <WhatsAppIcon />, className: classes.whatsappIcon },
        { icon: <InstagramIcon />, className: classes.instagramIcon },
        { icon: <TelegramIcon />, className: classes.telegramIcon },
        { icon: <FacebookIcon />, className: classes.facebookIcon },
      ]
    },
    {
      id: 2,
      title: "Dashboard Inteligente",
      description: "Controle total sobre suas conversas com métricas em tempo real, relatórios avançados e automação inteligente de atendimento.",
      icons: [
        { icon: <DashboardIcon />, className: classes.whatsappIcon },
        { icon: <SyncAltIcon />, className: classes.instagramIcon },
        { icon: <ChatBubbleIcon />, className: classes.telegramIcon },
        { icon: <RocketLaunchIcon />, className: classes.facebookIcon },
      ]
    },
    {
      id: 3,
      title: "Segurança e Confiança",
      description: "Plataforma com criptografia de ponta a ponta, conformidade com LGPD e backups automáticos para garantir a segurança dos seus dados.",
      icons: [
        { icon: <SecurityIcon />, className: classes.securityIcon },
        { icon: <VerifiedUserIcon />, className: classes.securityIcon },
        { icon: <LockIcon />, className: classes.securityIcon },
        { icon: <SyncAltIcon />, className: classes.securityIcon },
      ]
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    const fetchVersion = async () => {
      try {
        const { data } = await api.get("/version");
        setSystemVersion(data.version);
      } catch (err) {
        console.log("Error fetching system version", err);
      }
    };
    fetchVersion();

    return () => clearInterval(interval);
  }, [slides.length]);

  const nextSlide = () => setActiveSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);

  const handleSendEmail = async (values) => {
    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_URL}/forgetpassword/${values.email}`
      );
      if (response.data.status === 404) {
        toast.error("Email não encontrado");
      } else {
        toast.success("E-mail enviado com sucesso!");
        setShowAdditionalFields(true);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleResetPassword = async (values) => {
    try {
      await api.post(
        `${process.env.REACT_APP_BACKEND_URL}/resetpasswords/${values.email}/${values.token}/${values.newPassword}`
      );
      toast.success("Senha redefinida com sucesso.");
      history.push("/login");
    } catch (err) {
      toastError(err);
    }
  };

  const UserSchema = Yup.object().shape({
    email: Yup.string().email("Email inválido").required("Obrigatório"),
    token: showAdditionalFields ? Yup.string().required("Obrigatório") : Yup.string(),
    newPassword: showAdditionalFields
      ? Yup.string()
          .required("Campo obrigatório")
          .matches(passwordRegex, "Mínimo 8 caracteres, uma letra maiúscula, uma minúscula e um número.")
      : Yup.string(),
    confirmPassword: showAdditionalFields
      ? Yup.string()
          .oneOf([Yup.ref("newPassword"), null], "As senhas não correspondem")
          .required("Campo obrigatório")
      : Yup.string(),
  });

  return (
    <div className={classes.root}>
      <Helmet>
        <title>{appName || "Redefinir Senha"}</title>
      </Helmet>
      
      <CssBaseline />

      {/* Seção Esquerda - Slides */}
      <div className={classes.leftSection}>
        <div className={classes.decorationCircle} style={{ top: '-10%', right: '-5%', width: '300px', height: '300px' }} />
        <div className={classes.decorationCircle} style={{ bottom: '-8%', left: '-4%', width: '200px', height: '200px' }} />
        
        <div className={classes.slideContentWrapper}>
          <div className={classes.slideContainer}>
            {slides.map((slide, index) => (
              <div key={slide.id} className={`${classes.slide} ${index === activeSlide ? 'active' : ''}`}>
                <div className={classes.slideContent}>
                  <Typography className={classes.slideTitle}>{slide.title}</Typography>
                  <Typography className={classes.slideDescription}>{slide.description}</Typography>
                  <div className={classes.socialIconsContainer}>
                    {slide.icons.map((iconData, idx) => (
                      <div key={idx} className={`${classes.socialIcon} ${iconData.className}`}>
                        {React.cloneElement(iconData.icon, { style: { fontSize: "24px" } })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={classes.slideControls}>
            <div className={classes.slideButton} onClick={prevSlide}>
              <ArrowBackIosNewIcon className={classes.slideButtonIcon} />
            </div>
            <div className={classes.slideIndicators}>
              {slides.map((_, index) => (
                <div key={index} className={`${classes.indicator} ${index === activeSlide ? 'active' : ''}`} onClick={() => setActiveSlide(index)} />
              ))}
            </div>
            <div className={classes.slideButton} onClick={nextSlide}>
              <ArrowForwardIosIcon className={classes.slideButtonIcon} />
            </div>
          </div>

          <div className={classes.platformIndicator}>
            {["WhatsApp Business", "Instagram Direct", "Telegram"].map((p, i) => (
              <div key={i} className={classes.platformItem}>
                <div className={classes.platformDot} />
                <span>{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Seção Direita - Formulário */}
      <div className={classes.rightSection}>
        <div className={classes.loginCard}>
          <div className={classes.cardHeader}>
            <div className={classes.logoContainer}>
              <img 
                src={(theme.palette.type === 'light' ? theme.calculatedLogoLight() : theme.calculatedLogoDark())} 
                className={classes.logoImg} 
                alt="logo" 
              />
            </div>
            <Typography className={classes.welcomeTitle}>
              {showAdditionalFields ? "Redefinir Senha" : "Recuperar Senha"}
            </Typography>
            <Typography className={classes.welcomeSubtitle}>
              {showAdditionalFields 
                ? "Informe o código e sua nova senha."
                : "Informe seu e-mail para receber o código."
              }
            </Typography>
          </div>

          <Formik
            initialValues={{ email: "", token: "", newPassword: "", confirmPassword: "" }}
            validationSchema={UserSchema}
            onSubmit={(values, { setSubmitting }) => {
              if (showAdditionalFields) {
                handleResetPassword(values);
              } else {
                handleSendEmail(values);
              }
              setSubmitting(false);
            }}
          >
            {({ touched, errors, isSubmitting }) => (
              <Form className={classes.form}>
                <Field
                  as={TextField}
                  variant="outlined"
                  fullWidth
                  id="email"
                  name="email"
                  placeholder="seu@email.com"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  disabled={showAdditionalFields}
                  className={classes.inputField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon className={classes.inputIcon} />
                      </InputAdornment>
                    ),
                  }}
                />

                {showAdditionalFields && (
                  <>
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      id="token"
                      name="token"
                      placeholder="Código de verificação"
                      error={touched.token && Boolean(errors.token)}
                      helperText={touched.token && errors.token}
                      className={classes.inputField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <VpnKeyIcon className={classes.inputIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      id="newPassword"
                      name="newPassword"
                      placeholder="Nova senha"
                      error={touched.newPassword && Boolean(errors.newPassword)}
                      helperText={touched.newPassword && errors.newPassword}
                      className={classes.inputField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon className={classes.inputIcon} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <Visibility className={classes.visibilityIcon} /> : <VisibilityOff className={classes.visibilityIcon} />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder="Confirme a senha"
                      error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                      helperText={touched.confirmPassword && errors.confirmPassword}
                      className={classes.inputField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon className={classes.inputIcon} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                              {showConfirmPassword ? <Visibility className={classes.visibilityIcon} /> : <VisibilityOff className={classes.visibilityIcon} />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSubmitting}
                  className={classes.submitButton}
                  endIcon={<ArrowForwardIcon />}
                >
                  {showAdditionalFields ? "Redefinir Senha" : "Enviar Código"}
                </Button>

                <div className={classes.backToLogin}>
                  <Link component={RouterLink} to="/login">
                    Voltar para o Login
                  </Link>
                </div>
              </Form>
            )}
          </Formik>

          <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: '2rem', color: "#9ca3af", fontSize: "0.75rem" }}>
            © {new Date().getFullYear()} {nomeEmpresa} {systemVersion && ` - v${systemVersion}`}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;